import {
  signInWithEmailAndPassword,
  signInWithCredential,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  linkWithCredential,
  verifyBeforeUpdateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut as fbSignOut,
} from "firebase/auth";
import { auth, GOOGLE_CLIENT_ID } from "./firebase";

const API = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

export async function signInWithEmail(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email, password);
}

export async function signUpWithEmail(email: string, password: string): Promise<void> {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(cred.user);
}

export async function requestPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(opts: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            ux_mode?: "popup" | "redirect";
            auto_select?: boolean;
            context?: "signin" | "signup" | "use";
          }): void;
          renderButton(
            parent: HTMLElement,
            opts: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with";
              shape?: "rectangular" | "pill" | "circle" | "square";
              logo_alignment?: "left" | "center";
              width?: number;
              locale?: string;
            },
          ): void;
          prompt(): void;
          cancel(): void;
          disableAutoSelect(): void;
        };
      };
    };
  }
}

export function renderGoogleSignInButton(
  parent: HTMLElement,
  opts: { onSuccess: () => void; onError: (err: unknown) => void },
): void {
  if (!window.google) {
    opts.onError(new Error("GIS script not yet loaded"));
    return;
  }
  if (!GOOGLE_CLIENT_ID) {
    opts.onError(new Error("VITE_GOOGLE_CLIENT_ID not configured"));
    return;
  }
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    ux_mode: "popup",
    callback: async (resp) => {
      try {
        const credential = GoogleAuthProvider.credential(resp.credential);
        await signInWithCredential(auth, credential);
        opts.onSuccess();
      } catch (err) {
        opts.onError(err);
      }
    },
  });
  window.google.accounts.id.renderButton(parent, {
    type: "standard",
    theme: "filled_black",
    size: "large",
    text: "signin_with",
    shape: "rectangular",
    logo_alignment: "left",
    locale: "nl",
  });
}

export function renderGoogleLinkButton(
  parent: HTMLElement,
  opts: { onSuccess: () => void; onError: (err: unknown) => void },
): void {
  if (!window.google) {
    opts.onError(new Error("GIS script not yet loaded"));
    return;
  }
  if (!GOOGLE_CLIENT_ID) {
    opts.onError(new Error("VITE_GOOGLE_CLIENT_ID not configured"));
    return;
  }
  if (!auth.currentUser) {
    opts.onError(new Error("Not signed in"));
    return;
  }
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    ux_mode: "popup",
    callback: async (resp) => {
      try {
        const credential = GoogleAuthProvider.credential(resp.credential);
        if (!auth.currentUser) throw new Error("Sign-in lost mid-link");
        await linkWithCredential(auth.currentUser, credential);
        opts.onSuccess();
      } catch (err) {
        opts.onError(err);
      }
    },
  });
  window.google.accounts.id.renderButton(parent, {
    type: "standard",
    theme: "outline",
    size: "medium",
    text: "continue_with",
    shape: "rectangular",
    locale: "nl",
  });
}

export async function changeEmail(newEmail: string): Promise<void> {
  if (!auth.currentUser) throw new Error("Not signed in");
  await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
}

export async function changePassword(newPassword: string): Promise<void> {
  if (!auth.currentUser) throw new Error("Not signed in");
  await updatePassword(auth.currentUser, newPassword);
}

export async function reauthenticateWithEmail(currentPassword: string): Promise<void> {
  if (!auth.currentUser) throw new Error("Not signed in");
  if (!auth.currentUser.email)
    throw new Error("Account has no email — cannot re-authenticate with password");
  const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
  await reauthenticateWithCredential(auth.currentUser, credential);
}

export async function signOut(): Promise<void> {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    if (idToken) {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      });
    }
  } catch {
    // ignore
  }
  await fbSignOut(auth);
}
